/**
 * Activity Log Component
 * 
 * Displays user activities in a table format with filtering options.
 * 
 * @module ActivityLog
 */

import React from 'react';
import styled from 'styled-components';
import {
  Clock,
  User,
  FileText,
  Eye,
  Edit,
  Trash2,
  LogIn,
  LogOut,
  Navigation,
  Download,
  Upload
} from 'lucide-react';
import { ActivityData } from '../../services/ActivityMonitoringService';
import { ThemeColors, Spacing, Typography, Shadows } from '../../styles/ThemeStyles';

/**
 * Props interface
 */
export interface ActivityLogProps {
  activities: ActivityData[];
  isLoading?: boolean;
  onActivityClick?: (activity: ActivityData) => void;
}

/**
 * Styled components
 */
const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  background: white;
  border-radius: 12px;
  box-shadow: ${Shadows.sm};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
`;

const TableHead = styled.thead`
  background-color: ${ThemeColors.background.tertiary};
`;

const TableRow = styled.tr<{ clickable?: boolean }>`
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${ThemeColors.background.secondary};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${ThemeColors.border.light};
  }
`;

const TableHeader = styled.th`
  padding: ${Spacing.md} ${Spacing.lg};
  text-align: left;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.text.secondary};
  text-transform: uppercase;
`;

const TableCell = styled.td`
  padding: ${Spacing.md} ${Spacing.lg};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
`;

const ActionBadge = styled.span<{ action: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: 16px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  background-color: ${props => {
    switch (props.action) {
      case 'create': return ThemeColors.success.light;
      case 'update': return ThemeColors.info.light;
      case 'delete': return ThemeColors.error.light;
      case 'login': return ThemeColors.success.light;
      case 'logout': return ThemeColors.warning.light;
      default: return ThemeColors.gray[200];
    }
  }};
  color: ${props => {
    switch (props.action) {
      case 'create': return ThemeColors.success.dark;
      case 'update': return ThemeColors.info.dark;
      case 'delete': return ThemeColors.error.dark;
      case 'login': return ThemeColors.success.dark;
      case 'logout': return ThemeColors.warning.dark;
      default: return ThemeColors.text.secondary;
    }
  }};
`;

const EmptyState = styled.div`
  padding: ${Spacing.xxl} ${Spacing.lg};
  text-align: center;
  color: ${ThemeColors.text.secondary};
`;

const LoadingRow = styled(TableRow)`
  background: linear-gradient(
    90deg,
    ${ThemeColors.background.secondary} 25%,
    ${ThemeColors.background.tertiary} 50%,
    ${ThemeColors.background.secondary} 75%
  );
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;

  @keyframes loading {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

/**
 * Get action icon
 */
const getActionIcon = (action: string) => {
  switch (action) {
    case 'create': return <FileText size={14} />;
    case 'update': return <Edit size={14} />;
    case 'delete': return <Trash2 size={14} />;
    case 'view': return <Eye size={14} />;
    case 'login': return <LogIn size={14} />;
    case 'logout': return <LogOut size={14} />;
    case 'navigate': return <Navigation size={14} />;
    case 'download': return <Download size={14} />;
    case 'upload': return <Upload size={14} />;
    default: return <FileText size={14} />;
  }
};

/**
 * Format timestamp
 */
const formatTimestamp = (timestamp: Date | any): string => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Activity Log Component
 */
export const ActivityLog: React.FC<ActivityLogProps> = ({
  activities,
  isLoading = false,
  onActivityClick
}) => {
  if (isLoading) {
    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Timestamp</TableHeader>
              <TableHeader>User</TableHeader>
              <TableHeader>Action</TableHeader>
              <TableHeader>Description</TableHeader>
              <TableHeader>Entity</TableHeader>
            </TableRow>
          </TableHead>
          <tbody>
            {[1, 2, 3, 4, 5].map((i) => (
              <LoadingRow key={i}>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </LoadingRow>
            ))}
          </tbody>
        </Table>
      </TableContainer>
    );
  }

  if (activities.length === 0) {
    return (
      <TableContainer>
        <EmptyState>
          <Clock size={48} style={{ marginBottom: Spacing.md, opacity: 0.5 }} />
          <p>No activities found</p>
        </EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>Timestamp</TableHeader>
            <TableHeader>User</TableHeader>
            <TableHeader>Action</TableHeader>
            <TableHeader>Description</TableHeader>
            <TableHeader>Entity</TableHeader>
          </TableRow>
        </TableHead>
        <tbody>
          {activities.map((activity) => (
            <TableRow
              key={activity.id}
              clickable={!!onActivityClick}
              onClick={() => onActivityClick?.(activity)}
            >
              <TableCell>{formatTimestamp(activity.timestamp)}</TableCell>
              <TableCell>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: Typography.fontWeight.medium }}>
                    {activity.userName}
                  </span>
                  <span style={{ fontSize: Typography.fontSize.xs, color: ThemeColors.text.secondary }}>
                    {activity.userRole}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <ActionBadge action={activity.action}>
                  {getActionIcon(activity.action)}
                  {activity.action}
                </ActionBadge>
              </TableCell>
              <TableCell>{activity.description}</TableCell>
              <TableCell>
                <span style={{ 
                  textTransform: 'capitalize',
                  color: ThemeColors.text.secondary 
                }}>
                  {activity.entity}
                </span>
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>
    </TableContainer>
  );
};

